import { useState, useRef, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { useCurrentUser } from '../lib/useCurrentUser';
import { useEntityPermission } from '../lib/usePermissions';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * EntityNotesPanel — per-entity notes thread for a record detail page.
 *
 * Reads the org-scoped notes for one record from
 * GET /api/notes-enriched/:entityType/:entityId (author first/last name joined)
 * and lets staff add / edit / delete notes in-profile:
 *   - add    → POST /api/notes            {entity_type, entity_id, content}
 *   - edit   → PUT  /api/notes/:id        {content, edited_at}      (author or admin)
 *   - delete → PUT  /api/notes/:id        {deleted_at}   soft       (author or admin)
 *
 * @mentions: typing `@` in the composer opens a teammate picker (GET /api/users-list);
 * selecting one inserts `@First Last` and carries the user id in `mentioned_user_ids`,
 * which the backend (addNoteWithMentions) turns into a book-scoped notification. Mentions
 * are highlighted when rendered. Author lines show user NAMES, not emails.
 * Reused by any detail page; entityType is the notes.entity_type string (plural table name).
 */

interface EnrichedNote {
  id: string;
  content: string;
  created_by_user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  edited_at: string | null;
}

// §5 — an ordered @-mention candidate from /api/mention-candidates: the record's
// client roles first (role_label set: Client manager / Admin associate / Agent),
// then the client's followers, then the rest of the staff.
interface MentionCandidate {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  group: 'role' | 'follower' | 'other';
  role_label: string | null;
}

const ADMIN_ROLES = new Set(['super_admin', 'admin', 'administrator']);

function authorName(note: EnrichedNote): string {
  return [note.first_name, note.last_name].filter(Boolean).join(' ') || 'Unknown user';
}

function apiError(err: unknown): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error: unknown }).error);
  }
  return (err as Error).message;
}

/** The active `@token` immediately left of the caret (null if the caret isn't in one). */
export function activeMention(value: string, caret: number): { at: number; query: string } | null {
  const upto = value.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(value[at - 1])) return null; // must start at BOL or after whitespace
  const query = upto.slice(at + 1);
  if (/\s/.test(query)) return null;                    // a mention token has no inner whitespace
  return { at, query };
}

/** Render note content, highlighting `@Name` runs that resolve to a known teammate. */
function renderNoteContent(content: string, names: string[]): React.ReactNode {
  const escaped = names
    .filter(Boolean)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .sort((a, b) => b.length - a.length); // longest first so "@Jo Ann" wins over "@Jo"
  if (!escaped.length) return content;
  const re = new RegExp(`@(?:${escaped.join('|')})`, 'g');
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) out.push(content.slice(last, m.index));
    out.push(<span key={`m${k++}`} style={mentionChipStyle}>{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < content.length) out.push(content.slice(last));
  return out;
}

export interface EntityNotesPanelProps {
  entityType: string;
  entityId: string;
}

export function EntityNotesPanel({ entityType, entityId }: EntityNotesPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  // POST /api/notes is permission-gated on note:POST server-side; mirror it
  // on the composer so a role without note.can_add (training, external) doesn't see a
  // form that 403s on submit. Default-DENY while permissions load.
  const canAddNote = useEntityPermission('note', 'can_add');

  const [draft, setDraft] = useState('');
  const [mentions, setMentions] = useState<{ id: string; name: string }[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionHi, setMentionHi] = useState(0);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const [editing, setEditing] = useState<EnrichedNote | null>(null);
  const [editText, setEditText] = useState('');
  const [deleting, setDeleting] = useState<EnrichedNote | null>(null);

  const notesKey = ['entity-notes', entityType, entityId];

  const query = useQuery<EnrichedNote[]>({
    queryKey: notesKey,
    queryFn: ({ signal }) => api.get<EnrichedNote[]>(`/api/notes-enriched/${entityType}/${entityId}`, signal),
    enabled: !!entityType && !!entityId,
  });

  // §5 — @-mention candidates ORDERED by the record's client: the client's manager
  // + admin associate + (under a property) the property's agent first, each
  // role-labeled; then the client's followers; then the rest of the staff. The
  // picker filters this ordered list client-side, preserving that order.
  const candidatesQuery = useQuery<{ candidates: MentionCandidate[] }>({
    queryKey: ['mention-candidates', entityType, entityId],
    queryFn: ({ signal }) => api.get<{ candidates: MentionCandidate[] }>(`/api/mention-candidates/${entityType}/${entityId}`, signal),
    staleTime: 5 * 60_000,
    enabled: !!entityType && !!entityId,
  });
  const allCandidates = candidatesQuery.data?.candidates ?? [];
  const staffNames = useMemo(() => allCandidates.map((c) => c.name), [allCandidates]);

  const candidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return allCandidates.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionQuery, allCandidates]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: notesKey });

  const addMutation = useMutation<unknown, Error, { content: string; mentioned_user_ids: string[] }>({
    // Repointed onto the generic CRUD resource. The three /api/workflow/*-note
    // endpoints lived in src/routes/workflows.js, which the domain purge deleted
    // whole, and nothing re-mounted them — so add/edit/delete returned 404 on
    // every core page that renders this panel (business profile, information
    // request, invoice). The tab still LOOKED fine because the read path
    // (/api/notes-enriched) survived, which is why a 5xx sweep never saw it.
    //
    // /api/notes is a registered CRUD resource, so this inherits the full write
    // guard stack — permission, sanitize, unknown-column-400, protected columns,
    // cross-org FK, row scope, audit — rather than a bespoke handler.
    //
    // `mentioned_user_ids` is deliberately NOT sent: the @mention fan-out was a
    // notification producer that went with workflows.js, and its removal is
    // ratified by the notification-broadcast-scope guard test. The composer still
    // records the mention inline in the note text; it just no longer notifies.
    mutationFn: (vars) => api.post('/api/notes', {
      entity_type: entityType, entity_id: entityId, content: vars.content,
    }),
    onSuccess: () => {
      invalidate();
      setDraft(''); setMentions([]); setMentionQuery(null);
      toast({ variant: 'success', message: 'Note added.' });
    },
    onError: (err) => toast({ variant: 'error', message: apiError(err) }),
  });

  const editMutation = useMutation<unknown, Error, { noteId: string; content: string }>({
    // `edited_at` is stamped so the UI can show "edited" — the old handler set it
    // server-side; the generic CRUD path writes exactly what it is given.
    mutationFn: ({ noteId, content }) => api.put(`/api/notes/${noteId}`, { content, edited_at: new Date().toISOString() }),
    onSuccess: () => { invalidate(); setEditing(null); setEditText(''); toast({ variant: 'success', message: 'Note updated.' }); },
    onError: (err) => toast({ variant: 'error', message: apiError(err) }),
  });

  const deleteMutation = useMutation<unknown, Error, string>({
    // SOFT delete, matching the three readers that filter `deleted_at IS NULL`
    // (routes/enriched.js and two in routes/crud.js). Before this the column had
    // three readers and NO writer anywhere in src/ — the filter was correct and
    // permanently unreachable. A hard DELETE here would satisfy the UI and
    // silently make that filter dead code for good.
    mutationFn: (noteId) => api.put(`/api/notes/${noteId}`, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { invalidate(); setDeleting(null); toast({ variant: 'success', message: 'Note deleted.' }); },
    onError: (err) => toast({ variant: 'error', message: apiError(err) }),
  });

  const notes = query.data ?? [];

  // Only the ORIGINAL AUTHOR may edit a note (mirrors the backend); delete is the
  // moderation lever admins also keep. Kept as two predicates so the buttons split.
  const canEdit = (note: EnrichedNote): boolean =>
    !!currentUser && !!note.created_by_user_id && note.created_by_user_id === currentUser.id;
  const canDelete = (note: EnrichedNote): boolean => {
    if (!currentUser) return false;
    if (currentUser.role && ADMIN_ROLES.has(currentUser.role)) return true;
    return !!note.created_by_user_id && note.created_by_user_id === currentUser.id;
  };

  function onDraftChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setDraft(value);
    const caret = e.target.selectionStart ?? value.length;
    const active = activeMention(value, caret);
    setMentionQuery(active ? active.query : null);
    setMentionHi(0);
  }

  function pickMention(c: MentionCandidate) {
    const ta = taRef.current;
    const caret = ta?.selectionStart ?? draft.length;
    const active = activeMention(draft, caret);
    if (!active) return;
    const name = c.name;
    const before = draft.slice(0, active.at);
    const insert = `@${name} `;
    const next = before + insert + draft.slice(caret);
    setDraft(next);
    setMentions((prev) => (prev.some((m) => m.id === c.id) ? prev : [...prev, { id: c.id, name }]));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const pos = (before + insert).length;
      if (ta) { ta.focus(); ta.setSelectionRange(pos, pos); }
    });
  }

  function onComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionQuery === null || candidates.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionHi((i) => (i + 1) % candidates.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionHi((i) => (i - 1 + candidates.length) % candidates.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(candidates[Math.min(mentionHi, candidates.length - 1)]); }
    else if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); }
  }

  function submitNote() {
    const content = draft.trim();
    if (!content) return;
    // Only send ids whose @Name still appears in the body (handles deletions after picking).
    const ids = Array.from(new Set(mentions.filter((m) => draft.includes(`@${m.name}`)).map((m) => m.id)));
    addMutation.mutate({ content, mentioned_user_ids: ids });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* Composer — gated on note.can_add (mirrors the backend permGate): a view-only
          role like training must not see a composer that would 403 on submit. */}
      {canAddNote && (
      <div>
        <div style={{ position: 'relative' }}>
          <textarea
            ref={taRef}
            value={draft}
            onChange={onDraftChange}
            onKeyDown={onComposerKeyDown}
            placeholder="Add a note… use @ to mention a teammate"
            rows={3}
            aria-label="New note"
            style={textareaStyle}
          />
          {mentionQuery !== null && candidates.length > 0 && (
            <ul style={mentionMenuStyle} role="listbox" aria-label="Mention a teammate">
              {candidates.map((u, i) => (
                <li
                  key={u.id}
                  role="option"
                  aria-selected={i === mentionHi}
                  onMouseDown={(e) => { e.preventDefault(); pickMention(u); }}
                  onMouseEnter={() => setMentionHi(i)}
                  style={{ ...mentionItemStyle, background: i === mentionHi ? 'var(--brand-primary-bg)' : 'transparent' }}
                >
                  <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{u.name}</span>
                  {/* §5 — role holders carry a labeled badge (Client manager /
                      Admin associate / Agent); followers get a subtle "Following". */}
                  {u.role_label ? (
                    <span style={{
                      marginLeft: 'var(--sp-2)', padding: '1px 6px', borderRadius: 'var(--r-sm, 4px)',
                      background: 'var(--brand-primary-bg)', color: 'var(--brand-primary)',
                      fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)',
                    }}>{u.role_label}</span>
                  ) : u.group === 'follower' ? (
                    <span style={{ marginLeft: 'var(--sp-2)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}>Following</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-2)' }}>
          <Button
            variant="primary"
            size="sm"
            loading={addMutation.isPending}
            disabled={!draft.trim() || addMutation.isPending}
            onClick={submitNote}
          >
            Add note
          </Button>
        </div>
      </div>
      )}

      {/* List */}
      {query.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading notes…</p>
      ) : query.error ? (
        <div style={{
          padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
        }}>
          Failed to load notes. {(query.error as Error).message}
        </div>
      ) : notes.length === 0 ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>No notes on this record yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {notes.map((note) => (
            <li key={note.id} style={{ padding: 'var(--sp-3) 0', borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-1)' }}>
                <Badge variant="neutral">{authorName(note)}</Badge>
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{formatDateTime(note.created_at)}</span>
                {note.edited_at && (
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
                    · edited by {authorName(note)} · {formatDateTime(note.edited_at)}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', whiteSpace: 'pre-wrap' }}>
                {renderNoteContent(note.content, staffNames)}
              </div>
              {(canEdit(note) || canDelete(note)) && (
                <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
                  {canEdit(note) && (
                    <button
                      type="button"
                      onClick={() => { setEditing(note); setEditText(note.content); }}
                      style={linkButtonStyle}
                    >Edit</button>
                  )}
                  {canDelete(note) && (
                    <button
                      type="button"
                      onClick={() => setDeleting(note)}
                      style={{ ...linkButtonStyle, color: 'var(--color-status-red)' }}
                    >Delete</button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Edit modal (centered via Modal organism) */}
      <Modal
        open={!!editing}
        onClose={() => { if (!editMutation.isPending) { setEditing(null); setEditText(''); } }}
        title="Edit note"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setEditing(null); setEditText(''); }} disabled={editMutation.isPending}>Cancel</Button>
            <Button
              variant="primary"
              loading={editMutation.isPending}
              disabled={!editText.trim() || editMutation.isPending}
              onClick={() => editing && editMutation.mutate({ noteId: editing.id, content: editText.trim() })}
            >
              Save
            </Button>
          </>
        }
      >
        <textarea
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          rows={4}
          aria-label="Edit note"
          style={textareaStyle}
        />
      </Modal>

      {/* Delete confirm (centered) */}
      <Modal
        open={!!deleting}
        onClose={() => { if (!deleteMutation.isPending) setDeleting(null); }}
        title="Delete note"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)} disabled={deleteMutation.isPending}>Cancel</Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => deleting && deleteMutation.mutate(deleting.id)}
            >
              Delete
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
          This note will be removed from the record. This can't be undone.
        </p>
      </Modal>
    </div>
  );
}

const textareaStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: 'var(--sp-2) var(--sp-3)',
  fontSize: 'var(--fs-sm)', borderRadius: 'var(--r-md)', border: '1px solid var(--color-border)',
  resize: 'vertical', fontFamily: 'inherit', background: 'var(--color-input, var(--color-card))', color: 'var(--color-ink)',
};

const linkButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  fontSize: 'var(--fs-xs)', color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)',
};

const mentionChipStyle: React.CSSProperties = {
  color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)',
  background: 'var(--brand-primary-bg)', borderRadius: '3px', padding: '0 2px',
};

const mentionMenuStyle: React.CSSProperties = {
  position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: '2px',
  padding: 'var(--sp-1)', listStyle: 'none',
  background: 'var(--color-card)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-md, 0 6px 18px rgba(0,0,0,0.18))',
  maxHeight: '220px', overflowY: 'auto',
};

const mentionItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', padding: 'var(--sp-2) var(--sp-3)',
  borderRadius: 'var(--r-sm, 6px)', cursor: 'pointer', fontSize: 'var(--fs-sm)',
};
